/**
 * EHR context "Open File Link" redirect keys.
 *
 * Every surface that authors a redirect key validates against these — the admin console page and the
 * flowchart/pathway modal previously carried their own, disagreeing, copies.
 */

export const REDIRECT_KEY_PREFIX = 'open';

// Underscores are permitted because existing keys use them, but FORMAT_HINT deliberately doesn't
// advertise them — camelCase is the convention for new keys.
// `+` rather than `*` so the bare prefix is invalid on its own.
export const REDIRECT_KEY_PATTERN = /^open[A-Za-z0-9_]+$/;

// Matches content_file_redirect_keys.key VARCHAR(256).
export const REDIRECT_KEY_MAX_LENGTH = 256;

export const REDIRECT_KEY_MESSAGES = {
  // "open" is a locked adornment on every key input, so the hint describes only what the author types.
  FORMAT_HINT: 'Add at least one letter or number after "open". No spaces or special characters.',
  STYLE_HINT: 'Use camelCase, e.g. openAdultEdSepsis.',
  REQUIRED: 'Key is required',
  UNAVAILABLE: 'This key already exists in your organization',
  UNAVAILABLE_DESCRIPTION:
    'This key already exists in your organization. Each key must be unique organization-wide.'
} as const;

export const KEY_AVAILABILITY_STATE = {
  CHECKING: 'checking',
  AVAILABLE: 'available',
  UNAVAILABLE: 'unavailable'
} as const;

export type KeyAvailability =
  | null
  | (typeof KEY_AVAILABILITY_STATE)[keyof typeof KEY_AVAILABILITY_STATE];

export const isValidRedirectKey = (key: string): boolean => REDIRECT_KEY_PATTERN.test(key);

// Not the inverse of isValidRedirectKey: an empty field or the bare prefix is an unfinished key
// rather than a wrong one, so no surface flags it until the author types past the "open" adornment.
export const shouldShowRedirectKeyFormatError = (key: string): boolean =>
  key !== '' && key !== REDIRECT_KEY_PREFIX && !isValidRedirectKey(key);
